import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { PlusIcon } from "@heroicons/react/24/outline";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Modal from "./components/ui/Modal";
import Table from "./components/ui/Table";
import Pagination from "./components/ui/Pagination";
import ImageDropzone from "./components/ui/ImageDropzone";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const ASSET_HOST = baseUrl.replace("/admin", "");
const ITEMS_PER_PAGE = 10;

const resolveImage = (image) => {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  return `${ASSET_HOST}/${image.replace(/^\//, "")}`;
};

const GamesPage = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    labelName: "",
    formLabelName: "",
    price: "",
    image: null,
  });
  const [formErrors, setFormErrors] = useState({});

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchGames();
  }, []);

  // Fetch games
  const fetchGames = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/game/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status && result.data) {
        setGames(result.data);
      } else {
        throw new Error(result.message || "Failed to fetch games");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" }); // clear error
  };

  const handleImageChange = (file) => {
    setFormData((prev) => ({ ...prev, image: file }));
    setFormErrors((prev) => ({ ...prev, image: "" }));
  };

  // Validation
  const validateForm = (isEdit = false) => {
    let errors = {};

    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = "Game name must be at least 3 characters";
    }
    if (!formData.category) {
      errors.category = "Category is required";
    }
    if (!formData.labelName) {
      errors.labelName = "Label Name is required";
    }
    if (!formData.formLabelName) {
      errors.formLabelName = "Form Label Name is required";
    }
    if (!formData.price || isNaN(formData.price) || Number(formData.price) <= 0) {
      errors.price = "Price must be a positive number";
    }
    if (!isEdit && !formData.image) {
      errors.image = "Image is required for new game";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Normalize game response
  const normalizeGame = (res) => {
    return res?.data?.game || res?.data || res?.game || res;
  };

  const resetForm = () => {
    setFormData({ name: "", category: "", labelName: "", formLabelName: "", price: "", image: null });
  };

  // Add new game
  const handleAddNew = async () => {
    if (!validateForm(false)) return;

    try {
      const form = new FormData();
      form.append("name", formData.name);
      form.append("category", formData.category);
      form.append("labelName", formData.labelName);
      form.append("formLabelName", formData.formLabelName);
      form.append("price", formData.price);
      if (formData.image) form.append("image", formData.image);

      const res = await fetch(`${baseUrl}/game/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Add failed");

      const newGame = normalizeGame(result);

      setGames((prev) => [...prev, newGame]);
      resetForm();
      setShowModal(false);
      setEditingIndex(null);

      toast.success("Game created successfully!");
      await fetchGames();
    } catch (error) {
      toast.error("Game created failed!");
    }
  };

  // Edit click
  const handleEdit = (index) => {
    setEditingIndex(index);
    setFormData({
      name: games[index].name,
      category: games[index].category,
      labelName: games[index].labelName,
      formLabelName: games[index].formLabelName,
      price: games[index].price,
      image: null,
    });
    setShowModal(true);
  };

  // Save edited game
  const handleSave = async () => {
    if (!validateForm(true)) return;

    const current = games[editingIndex];
    const gameId = current._id || current.id;

    try {
      const form = new FormData();
      form.append("name", formData.name);
      form.append("category", formData.category);
      form.append("labelName", formData.labelName);
      form.append("formLabelName", formData.formLabelName);
      form.append("price", formData.price);
      if (formData.image) form.append("image", formData.image);

      const res = await fetch(`${baseUrl}/game/edit/${gameId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      await fetchGames();

      resetForm();
      setShowModal(false);
      setEditingIndex(null);

      toast.success("Game updated successfully!");
    } catch (error) {
      toast.error("Game updated failed!");
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this game?")) return;
    try {
      const res = await fetch(`${baseUrl}/game/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Delete failed");

      setGames((prev) => prev.filter((g) => g.id !== id && g._id !== id));
      toast.success("Game deleted successfully!");
    } catch (error) {
      toast.error("Game deleted failed!");
    }
  };

  // Toggle active/inactive
  const toggleActive = async (id, isActive) => {
    try {
      const endpoint = isActive
        ? `${baseUrl}/game/deactivate/${id}`
        : `${baseUrl}/game/activate/${id}`;
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      setGames((prev) =>
        prev.map((g) =>
          (g.id === id || g._id === id) ? { ...g, isActive: !isActive } : g
        )
      );
    } catch (error) {
      toast.error("Game activation/deactivation failed!");
    }
  };

  const totalPages = Math.ceil(games.length / ITEMS_PER_PAGE) || 1;
  const pagedGames = games.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const columns = [
    { key: "name", header: "Name" },
    { key: "category", header: "Category" },
    { key: "labelName", header: "Label Name" },
    { key: "formLabelName", header: "Form Label Name" },
    { key: "price", header: "Price" },
    {
      key: "image",
      header: "Image",
      render: (item) =>
        resolveImage(item.image) ? (
          <img
            src={resolveImage(item.image)}
            alt={item.name}
            className="h-12 w-12 object-cover rounded"
          />
        ) : (
          <span className="text-gray-500 italic">No Image</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            item.isActive ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
          }`}
        >
          {item.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item) => {
        const index = games.findIndex((g) => (g.id || g._id) === (item.id || item._id));
        return (
          <div className="flex flex-wrap gap-2">
            <button
              className="bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700 transition"
              onClick={() => handleEdit(index)}
            >
              Edit
            </button>
            <button
              className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition"
              onClick={() => handleDelete(item.id || item._id)}
            >
              Delete
            </button>
            <button
              className={`px-3 py-1 rounded text-white transition ${
                item.isActive ? "bg-gray-600 hover:bg-gray-500" : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={() => toggleActive(item.id || item._id, item.isActive)}
            >
              {item.isActive ? "Deactivate" : "Activate"}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col md:flex-row bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-white">Games Management</h2>
            <button
              onClick={() => {
                setEditingIndex(null);
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 bg-brand text-white font-semibold px-4 py-2 rounded hover:bg-brand-dark transition"
            >
              <PlusIcon className="h-5 w-5" />
              Add Game
            </button>
          </div>

          <Modal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            title={editingIndex !== null ? "Edit Game" : "Add New Game"}
            footer={
              <>
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={editingIndex !== null ? handleSave : handleAddNew}
                  className="bg-brand text-white px-4 py-2 rounded hover:bg-brand-dark transition"
                >
                  {editingIndex !== null ? "Save Changes" : "Add Game"}
                </button>
              </>
            }
          >
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Game Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Game Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.name && <p className="text-red-400 text-sm">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
                <input
                  type="text"
                  name="category"
                  placeholder="Category"
                  value={formData.category}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.category && <p className="text-red-400 text-sm">{formErrors.category}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Label Name</label>
                <input
                  type="text"
                  name="labelName"
                  placeholder="Label Name"
                  value={formData.labelName}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.labelName && <p className="text-red-400 text-sm">{formErrors.labelName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Price</label>
                <input
                  type="number"
                  name="price"
                  placeholder="Price"
                  value={formData.price}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.price && <p className="text-red-400 text-sm">{formErrors.price}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Form Label Name</label>
                <input
                  type="text"
                  name="formLabelName"
                  placeholder="Enter Paypal ID, Enter Game ID"
                  value={formData.formLabelName}
                  onChange={handleChange}
                  className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white rounded"
                />
                {formErrors.formLabelName && <p className="text-red-400 text-sm">{formErrors.formLabelName}</p>}
              </div>
              <div>
                <ImageDropzone
                  file={formData.image}
                  onChange={handleImageChange}
                  existingImageUrl={editingIndex !== null ? resolveImage(games[editingIndex]?.image) : null}
                  error={formErrors.image}
                />
              </div>
            </div>
          </Modal>

          {error && <p className="text-red-400 mb-4">{error}</p>}

          <Table
            columns={columns}
            rows={pagedGames}
            rowKey={(item) => item.id || item._id}
            loading={loading}
            emptyMessage="No games found."
          />

          <div className="mt-6">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamesPage;
